import React, { useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { WizardStepProps } from "@components/WizardCst/types";
import { AssociationItem, EditorialPerInitiative } from "../../types";
import EditorialBanner from "./components/EditorialBanner";
import EditorialTabs from "./components/EditorialTabs";
import CarouselsSection from "./components/CarouselsSection";
import TopicsSection from "./components/TopicsSection";
// import SkillsSection from "./components/SkillsSection";
import HighlightsSection from "./components/HighlightsSection";
import { PERMISSIONS, usePermissions } from "@utils/permissions";

const EMPTY_PER_INITIATIVE: EditorialPerInitiative = {
  selected_carousel_ids: [],
  selected_topic_ids: [],
  selected_skill_ids: [],
};

const SectionDivider: React.FC = () => <Divider sx={{ opacity: 0.6 }} />;

interface EditorialStepData {
  selected_initiatives: AssociationItem[];
  editorial_per_initiative: Record<number, EditorialPerInitiative>;
  highlight_explore: boolean;
  is_standalone?: boolean;
}

function EditorialStep<T extends EditorialStepData>({
  data,
  setData,
}: WizardStepProps<T>): React.ReactElement {
  const { t } = useTranslation("wizard-activity");
  const theme = useTheme();
  const { can } = usePermissions();
  // Come v1 (route associazione categorie in CorporateInitiative.tsx):
  // associate.initiatives.categories + usermgmt.initiatives.categories.read.
  const canAssociateCategories = can([
    PERMISSIONS.catalog.associateCategories,
    PERMISSIONS.initiativeCategories.read,
  ]);
  // Come v1 (route associazione topic in CorporateInitiative.tsx):
  // associate.topics + usermgmt.topics.read.
  const canAssociateTopics = can([
    PERMISSIONS.catalog.associateTopics,
    PERMISSIONS.topics.read,
  ]);

  const selectedInitiatives = data.selected_initiatives;
  const initiativeIds = useMemo(
    () => selectedInitiatives.map((s) => Number(s.id)),
    [selectedInitiatives],
  );

  const [activeInitiativeId, setActiveInitiativeId] = useState<number | null>(
    initiativeIds[0] ?? null,
  );
  const [topicsVisible, setTopicsVisible] = useState(true);

  useEffect(() => {
    if (initiativeIds.length === 0) {
      setActiveInitiativeId(null);
      return;
    }
    if (
      activeInitiativeId === null ||
      !initiativeIds.includes(activeInitiativeId)
    ) {
      setActiveInitiativeId(initiativeIds[0]);
    }
  }, [initiativeIds, activeInitiativeId]);

  useEffect(() => {
    if (data.is_standalone === false && data.highlight_explore) {
      setData({ highlight_explore: false } as Partial<T>);
    }
  }, [data.is_standalone, data.highlight_explore, setData]);

  const tabs = useMemo(
    () =>
      selectedInitiatives.map((it, idx) => ({
        id: Number(it.id),
        label:
          it.label ||
          it.name ||
          t("editorial.tab-label", { index: idx + 1 }) ||
          `Iniziativa ${idx + 1}`,
      })),
    [selectedInitiatives, t],
  );

  const currentSelections =
    activeInitiativeId !== null
      ? (data.editorial_per_initiative[activeInitiativeId] ??
        EMPTY_PER_INITIATIVE)
      : EMPTY_PER_INITIATIVE;

  const updateInitiativeSelections = (
    patch: Partial<EditorialPerInitiative>,
  ) => {
    if (activeInitiativeId === null) return;
    const prev =
      data.editorial_per_initiative[activeInitiativeId] ?? EMPTY_PER_INITIATIVE;
    setData({
      editorial_per_initiative: {
        ...data.editorial_per_initiative,
        [activeInitiativeId]: { ...prev, ...patch },
      },
    } as Partial<T>);
  };

  if (initiativeIds.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <EditorialBanner />
        <Box
          sx={{
            padding: "16px",
            borderRadius: "8px",
            border: `1px dashed ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontSize: "13px",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 0.5 }}>
            {t("editorial.no-initiatives.title")}
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {t("editorial.no-initiatives.description")}
          </Typography>
        </Box>
        <SectionDivider />
        <HighlightsSection data={data} setData={setData} errors={{}} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <EditorialBanner />

      <EditorialTabs
        tabs={tabs}
        activeId={activeInitiativeId}
        onChange={setActiveInitiativeId}
      />

      {activeInitiativeId !== null && (
        <>
          {data.is_standalone !== false && canAssociateCategories && (
            <>
              <CarouselsSection
                initiativeId={activeInitiativeId}
                selectedIds={currentSelections.selected_carousel_ids}
                onChange={(ids) =>
                  updateInitiativeSelections({ selected_carousel_ids: ids })
                }
              />

              <SectionDivider />
            </>
          )}

          {canAssociateTopics && (
            <TopicsSection
              initiativeId={activeInitiativeId}
              selectedIds={currentSelections.selected_topic_ids}
              onChange={(ids) =>
                updateInitiativeSelections({ selected_topic_ids: ids })
              }
              onVisibilityChange={setTopicsVisible}
            />
          )}

          {canAssociateTopics && topicsVisible && <SectionDivider />}

          <HighlightsSection data={data} setData={setData} errors={{}} />

          {/* <SectionDivider />
          {data.is_standalone && (
            <SkillsSection
              initiativeId={activeInitiativeId}
              selectedIds={currentSelections.selected_skill_ids}
              onChange={(ids) =>
                updateInitiativeSelections({ selected_skill_ids: ids })
              }
            />
          )} */}
        </>
      )}
    </Box>
  );
}

export default EditorialStep;
